// Page "Clients" : camembert CA par client + filtres année/mois + liste CRUD.

import { FormEvent, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { clientsApi } from "../api/clients";
import { invoicesApi } from "../api/invoices";
import { ApiError } from "../api/client";
import type { ClientCreate, ClientPublic, InvoiceListItem } from "../api/types";
import { AppShell } from "../components/AppShell";
import { FormField } from "../components/FormField";
import { Modal } from "../components/Modal";

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; client: ClientPublic };

// Palette couleurs pour le camembert
const COLORS = [
  "#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6",
  "#06b6d4","#f97316","#84cc16","#ec4899","#6366f1",
];

function fmt(n: number) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n);
}

// ---- Camembert SVG pur ---------------------------------------------------

type Slice = { name: string; value: number; color: string; pct: number };

function PieChart({ slices, hovered, onHover }: {
  slices: Slice[];
  hovered: number | null;
  onHover: (i: number | null) => void;
}) {
  const cx = 110, cy = 110, r = 90;
  let angle = -Math.PI / 2;

  const paths = slices.map((s, i) => {
    const sweep = (s.pct / 100) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(angle);
    const y1 = cy + r * Math.sin(angle);
    angle += sweep;
    const x2 = cx + r * Math.cos(angle);
    const y2 = cy + r * Math.sin(angle);
    const large = sweep > Math.PI ? 1 : 0;
    const scale = hovered === i ? 1.06 : 1;
    return {
      d: `M${cx},${cy} L${x1.toFixed(2)},${y1.toFixed(2)} A${r},${r} 0 ${large},1 ${x2.toFixed(2)},${y2.toFixed(2)} Z`,
      color: s.color, scale, i,
    };
  });

  return (
    <svg viewBox="0 0 220 220" className="w-56 h-56 drop-shadow-sm">
      {paths.map(p => (
        <path
          key={p.i}
          d={p.d}
          fill={p.color}
          stroke="white"
          strokeWidth="2"
          style={{
            transform: `scale(${p.scale})`,
            transformOrigin: `${cx}px ${cy}px`,
            transition: "transform 0.15s",
          }}
          onMouseEnter={() => onHover(p.i)}
          onMouseLeave={() => onHover(null)}
          className="cursor-pointer"
        />
      ))}
      {/* Trou central donut */}
      <circle cx={cx} cy={cy} r={42} fill="white" />
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="11" fill="#64748b">Total</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1e293b">
        {fmt(slices.reduce((s, sl) => s + sl.value, 0))}
      </text>
    </svg>
  );
}

// ---- Page principale ------------------------------------------------------

export function ClientsPage() {
  const { t } = useTranslation();
  const [clients, setClients] = useState<ClientPublic[] | null>(null);
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [q, setQ] = useState("");
  const [listError, setListError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const [hovered, setHovered] = useState<number | null>(null);

  // Filtres camembert
  const [filterYear, setFilterYear] = useState<string>("");
  const [filterMonth, setFilterMonth] = useState<string>("");

  async function reload() {
    setListError(null);
    try {
      const [data, inv] = await Promise.all([
        clientsApi.list(q ? { q } : {}),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        invoicesApi.list({ limit: 200, invoice_type: "customer" } as any),
      ]);
      setClients(data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items: InvoiceListItem[] = (inv as any).items ?? inv;
      setInvoices(items);
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Erreur");
      setClients([]);
    }
  }

  useEffect(() => { void reload(); }, []); // eslint-disable-line

  // Années disponibles
  const years = useMemo(() => {
    const s = new Set<string>();
    for (const inv of invoices) {
      s.add((inv.issue_date as string).slice(0, 4));
    }
    return Array.from(s).sort().reverse();
  }, [invoices]);

  // Mois disponibles (filtrés par année si sélectionnée)
  const months = useMemo(() => {
    const s = new Set<string>();
    for (const inv of invoices) {
      const m = (inv.issue_date as string).slice(0, 7);
      if (!filterYear || m.startsWith(filterYear)) s.add(m);
    }
    return Array.from(s).sort().reverse();
  }, [invoices, filterYear]);

  // Slices pour le camembert
  const slices: Slice[] = useMemo(() => {
    const map = new Map<string, number>();
    for (const inv of invoices) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((inv as any).invoice_type === "supplier") continue;
      const d = inv.issue_date as string;
      if (filterYear && !d.startsWith(filterYear)) continue;
      if (filterMonth && !d.startsWith(filterMonth)) continue;
      const name = (clients ?? []).find(c => c.id === inv.client_id)?.name ?? "Autre";
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ttc = parseFloat((inv.amount_ttc as any)) || 0;
      map.set(name, (map.get(name) ?? 0) + ttc);
    }
    const total = Array.from(map.values()).reduce((s, v) => s + v, 0);
    if (total === 0) return [];
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], i) => ({
        name, value, color: COLORS[i % COLORS.length],
        pct: (value / total) * 100,
      }));
  }, [invoices, clients, filterYear, filterMonth]);

  async function onDelete(c: ClientPublic) {
    if (!window.confirm(`Supprimer le client "${c.name}" ?`)) return;
    try {
      await clientsApi.delete(c.id);
      await reload();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Suppression impossible");
    }
  }

  const activeSlice = hovered !== null ? slices[hovered] : null;

  return (
    <AppShell>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("clientsPage.title")}</h1>
          <p className="text-sm text-slate-500">{t("clientsPage.subtitle")}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setDialog({ mode: "create" })}>
          + {t("clientsPage.newClient")}
        </button>
      </header>

      {/* CAMEMBERT KPI */}
      {slices.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          {/* En-tête + filtres */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <h2 className="text-base font-semibold text-slate-800 mr-auto">
              {t("clientsPage.caByClient")}
            </h2>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">{t("common.year")}</label>
              <select
                value={filterYear}
                onChange={(e) => { setFilterYear(e.target.value); setFilterMonth(""); }}
                className="input text-sm py-1 min-w-[100px]"
              >
                <option value="">{t("common.allYears")}</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">{t("common.month")}</label>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="input text-sm py-1 min-w-[130px]"
              >
                <option value="">{t("common.allMonths")}</option>
                {months.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            {(filterYear || filterMonth) && (
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800 underline"
                onClick={() => { setFilterYear(""); setFilterMonth(""); }}
              >
                {t("common.reset")}
              </button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-8">
            {/* Camembert */}
            <div className="relative flex-shrink-0">
              <PieChart slices={slices} hovered={hovered} onHover={setHovered} />
              {/* Tooltip au survol */}
              {activeSlice && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-white/95 rounded-lg px-3 py-2 shadow-lg text-center border border-slate-100 mt-10">
                    <p className="text-xs text-slate-500 truncate max-w-[130px]">{activeSlice.name}</p>
                    <p className="text-sm font-bold text-slate-900">{fmt(activeSlice.value)}</p>
                    <p className="text-xs text-slate-400">{activeSlice.pct.toFixed(1)} %</p>
                  </div>
                </div>
              )}
            </div>

            {/* Légende interactive */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
              {slices.map((s, i) => (
                <div
                  key={s.name}
                  className={[
                    "flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer transition-colors",
                    hovered === i ? "bg-slate-100" : "hover:bg-slate-50",
                  ].join(" ")}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: s.color }}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{s.name}</p>
                    <p className="text-xs text-slate-500">
                      {fmt(s.value)} &middot; {s.pct.toFixed(1)} %
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* RECHERCHE */}
      <form
        onSubmit={(e) => { e.preventDefault(); void reload(); }}
        className="mb-4 flex gap-2"
        role="search"
        aria-label="Recherche clients"
      >
        <input
          type="search"
          placeholder={t("clientsPage.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="input flex-1"
          aria-label="Recherche"
        />
        <button type="submit" className="btn-primary">{t("common.search")}</button>
      </form>

      {/* LISTE */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {listError && <p role="alert" className="p-4 text-sm text-red-600">{listError}</p>}
        {clients === null && <p className="p-6 text-sm text-slate-500">Chargement…</p>}
        {clients !== null && clients.length === 0 && !listError && (
          <p className="p-6 text-sm text-slate-500">{t("clientsPage.noClients")}</p>
        )}
        {clients && clients.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                <th className="px-4 py-2 font-medium">{t("common.email")}</th>
                <th className="px-4 py-2 font-medium">{t("common.country")}</th>
                <th className="px-4 py-2 font-medium w-32 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900">{c.name}</td>
                  <td className="px-4 py-2 text-slate-600">{c.email ?? "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{c.country ?? "—"}</td>
                  <td className="px-4 py-2 text-right space-x-3">
                    <button type="button" className="text-brand-600 hover:underline" onClick={() => setDialog({ mode: "edit", client: c })}>{t("common.edit")}</button>
                    <button type="button" className="text-red-600 hover:underline" onClick={() => onDelete(c)}>{t("common.delete")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ClientDialog
        state={dialog}
        onClose={() => setDialog({ mode: "closed" })}
        onSaved={async () => { setDialog({ mode: "closed" }); await reload(); }}
      />
    </AppShell>
  );
}

// ---- Modale création / édition -------------------------------------------

function ClientDialog({ state, onClose, onSaved }: {
  state: DialogState; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isOpen = state.mode !== "closed";
  const initial = state.mode === "edit" ? state.client : emptyCreate();
  const [values, setValues] = useState<ClientCreate>(initial);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (state.mode === "create") setValues(emptyCreate());
    else if (state.mode === "edit") setValues({ ...state.client });
    setError(null);
  }, [state]);

  if (!isOpen) return null;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const payload = compactPayload(values);
      if (state.mode === "edit") await clientsApi.update(state.client.id, payload);
      else await clientsApi.create(payload);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Impossible d'enregistrer");
    } finally { setPending(false); }
  }

  const title = state.mode === "edit" ? "Éditer le client" : "Nouveau client";

  return (
    <Modal open={isOpen} onClose={onClose} title={title} size="lg">
      <form onSubmit={onSubmit} className="space-y-4" aria-label="client-form">
        <FormField label="Nom" htmlFor="name" required>
          <input id="name" required value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} className="input" />
        </FormField>
        <div className="grid grid-cols-2 gap-4">
          <FormField label={t("common.email")} htmlFor="email">
            <input id="email" type="email" value={values.email ?? ""} onChange={(e) => setValues({ ...values, email: e.target.value || null })} className="input" />
          </FormField>
          <FormField label={t("common.phone")} htmlFor="phone">
            <input id="phone" value={values.phone ?? ""} onChange={(e) => setValues({ ...values, phone: e.target.value || null })} className="input" />
          </FormField>
        </div>
        <FormField label={t("clientsPage.legalName")} htmlFor="legal_name">
          <input id="legal_name" value={values.legal_name ?? ""} onChange={(e) => setValues({ ...values, legal_name: e.target.value || null })} className="input" />
        </FormField>
        <FormField label={t("common.address")} htmlFor="address">
          <textarea id="address" rows={2} value={values.address ?? ""} onChange={(e) => setValues({ ...values, address: e.target.value || null })} className="input" />
        </FormField>
        <div className="grid grid-cols-3 gap-4">
          <FormField label={t("clientsPage.siret")} htmlFor="siret">
            <input id="siret" value={values.siret ?? ""} onChange={(e) => setValues({ ...values, siret: e.target.value || null })} className="input" />
          </FormField>
          <FormField label={t("clientsPage.vatNumber")} htmlFor="vat_number">
            <input id="vat_number" value={values.vat_number ?? ""} onChange={(e) => setValues({ ...values, vat_number: e.target.value || null })} className="input" />
          </FormField>
          <FormField label={t("common.country")} htmlFor="country" hint={t("clientsPage.countryHint")}>
            <input id="country" maxLength={2} value={values.country ?? ""} onChange={(e) => setValues({ ...values, country: e.target.value.toUpperCase() || null })} className="input" />
          </FormField>
        </div>
        <FormField label={t("common.notes")} htmlFor="notes">
          <textarea id="notes" rows={3} value={values.notes ?? ""} onChange={(e) => setValues({ ...values, notes: e.target.value || null })} className="input" />
        </FormField>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" className="text-slate-500 hover:text-slate-800 text-sm" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" disabled={pending} className="btn-primary">{pending ? t("common.saving") : t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}

function emptyCreate(): ClientCreate {
  return { name: "", legal_name: null, email: null, phone: null, address: null, siret: null, vat_number: null, country: null, default_currency: "EUR", notes: null };
}

function compactPayload(v: ClientCreate): ClientCreate {
  return { name: v.name.trim(), legal_name: emptyToNull(v.legal_name), email: emptyToNull(v.email), phone: emptyToNull(v.phone), address: emptyToNull(v.address), siret: emptyToNull(v.siret), vat_number: emptyToNull(v.vat_number), country: emptyToNull(v.country), default_currency: emptyToNull(v.default_currency), notes: emptyToNull(v.notes) };
}

function emptyToNull(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  return t ? t : null;
}
